<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { loadGoogleMapsPlaces } from '@/utils/googleMapsLoader'
import {
  parseGooglePlaceAddress,
  type ParsedGooglePlaceAddress,
} from '@/utils/parseGooglePlaceAddress'

const model = defineModel<string>({ default: '' })

const props = withDefaults(
  defineProps<{
    placeholder?: string
    maxlength?: number
    disabled?: boolean
    /** 国家限制，默认加拿大 */
    country?: string | string[]
  }>(),
  {
    placeholder: 'Enter service address',
    disabled: false,
    country: () => ['CA'],
  },
)

const emit = defineEmits<{
  placeSelected: [parsed: ParsedGooglePlaceAddress]
  blur: []
}>()

const inputRef = ref<HTMLInputElement | null>(null)
let autocomplete: google.maps.places.Autocomplete | null = null
let placeListener: google.maps.MapsEventListener | null = null

async function bindAutocomplete(): Promise<void> {
  const el = inputRef.value
  if (!el || autocomplete) return
  try {
    await loadGoogleMapsPlaces()
  } catch {
    return
  }
  if (!inputRef.value || autocomplete) return

  const countries = Array.isArray(props.country) ? props.country : [props.country]
  autocomplete = new google.maps.places.Autocomplete(inputRef.value, {
    componentRestrictions: { country: countries },
    fields: ['place_id', 'geometry', 'name', 'address_components'],
    types: ['address'],
  })

  placeListener = autocomplete.addListener('place_changed', () => {
    const place = autocomplete?.getPlace()
    const parsed = parseGooglePlaceAddress(place)
    if (!parsed) return
    model.value = parsed.serviceAddress
    emit('placeSelected', parsed)
  })
}

function teardown(): void {
  if (placeListener) {
    google.maps.event.removeListener(placeListener)
    placeListener = null
  }
  autocomplete = null
}

onMounted(() => {
  void bindAutocomplete()
})

onBeforeUnmount(() => {
  teardown()
})

watch(
  () => props.disabled,
  (disabled) => {
    if (!disabled && !autocomplete) void bindAutocomplete()
  },
)
</script>

<template>
  <input
    ref="inputRef"
    v-model="model"
    type="text"
    class="hm-address-autocomplete"
    :placeholder="placeholder"
    :maxlength="maxlength"
    :disabled="disabled"
    autocomplete="off"
    @blur="emit('blur')"
  />
</template>

<style scoped>
.hm-address-autocomplete {
  width: 100%;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font: inherit;
  color: inherit;
}
</style>
